import React, { useState } from "react";
import axios from "axios";
import { Heart } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const emailOk = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

const mapComments = (list, id, fn) =>
  list.map((c) => {
    if (c.id === id) return fn(c);
    if (c.replies && c.replies.length) return { ...c, replies: mapComments(c.replies, id, fn) };
    return c;
  });

const ReplyForm = ({ onSubmit, onCancel }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const go = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!emailOk(email)) {
      toast.error("Please enter a valid email to reply.");
      return;
    }
    setBusy(true);
    const ok = await onSubmit({ name: name.trim(), email: email.trim(), text: text.trim() });
    setBusy(false);
    if (ok) {
      setName("");
      setEmail("");
      setText("");
    }
  };

  return (
    <form onSubmit={go} className="mt-3 flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          placeholder="Name (optional)"
          data-testid="reply-name"
          className="h-10 bg-white/70 border-neutral-300 font-sans-ui text-sm focus-visible:ring-neutral-900"
        />
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="Email (required)"
          data-testid="reply-email"
          className="h-10 bg-white/70 border-neutral-300 font-sans-ui text-sm focus-visible:ring-neutral-900"
        />
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={500}
        rows={2}
        placeholder="Write a reply..."
        data-testid="reply-text"
        className="bg-white/70 border-neutral-300 font-display text-sm resize-none focus-visible:ring-neutral-900"
      />
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy}
          data-testid="reply-submit"
          className="bg-neutral-900 hover:bg-neutral-700 disabled:opacity-60 text-white font-sans-ui text-xs px-5 py-2 rounded-full transition-colors"
        >
          {busy ? "Posting\u2026" : "Reply"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="font-sans-ui text-xs text-neutral-500 hover:text-neutral-900 px-2 py-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const ReplyItem = ({ comment, onLike }) => {
  const liked = typeof window !== "undefined" && localStorage.getItem(`clike_${comment.id}`) === "1";
  return (
    <div className="mt-4 pl-5 border-l-2 border-neutral-200" data-testid="thread-reply">
      <p className="font-sans-ui text-sm font-medium text-neutral-900">{comment.name}</p>
      <p className="font-display text-neutral-700 mt-1 leading-relaxed text-lg">{comment.text}</p>
      <div className="flex items-center gap-4 mt-2">
        <button
          onClick={() => onLike(comment)}
          data-testid={`comment-like-${comment.id}`}
          aria-label="Like comment"
          className={`inline-flex items-center gap-1.5 text-xs font-sans-ui rounded-full px-2.5 py-1 transition-all duration-200 hover:scale-105 ${
            liked ? "bg-rose-100 text-rose-600" : "text-neutral-500 hover:bg-black/5"
          }`}
        >
          <Heart size={13} fill={liked ? "currentColor" : "none"} />
          {comment.likes > 0 && <span>{comment.likes}</span>}
        </button>
      </div>
    </div>
  );
};

const CommentItem = ({ comment, openReply, setOpenReply, onLike, onReply }) => {
  const liked = typeof window !== "undefined" && localStorage.getItem(`clike_${comment.id}`) === "1";
  return (
    <div className="border-b border-neutral-200 pb-4" data-testid="thread-comment">
      <p className="font-sans-ui text-sm font-medium text-neutral-900">{comment.name}</p>
      <p className="font-display text-neutral-700 mt-1 leading-relaxed text-lg">{comment.text}</p>
      <div className="flex items-center gap-4 mt-2">
        <button
          onClick={() => onLike(comment)}
          data-testid={`comment-like-${comment.id}`}
          aria-label="Like comment"
          className={`inline-flex items-center gap-1.5 text-xs font-sans-ui rounded-full px-2.5 py-1 transition-all duration-200 hover:scale-105 ${
            liked ? "bg-rose-100 text-rose-600" : "text-neutral-500 hover:bg-black/5"
          }`}
        >
          <Heart size={13} fill={liked ? "currentColor" : "none"} />
          {comment.likes > 0 && <span>{comment.likes}</span>}
        </button>
        <button
          onClick={() => setOpenReply(openReply === comment.id ? null : comment.id)}
          data-testid={`comment-reply-toggle-${comment.id}`}
          className="text-xs font-sans-ui text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          Reply
        </button>
      </div>

      {openReply === comment.id && (
        <ReplyForm
          onSubmit={(payload) => onReply(comment.id, payload)}
          onCancel={() => setOpenReply(null)}
        />
      )}

      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-2">
          {comment.replies.map((r) => (
            <ReplyItem key={r.id} comment={r} onLike={onLike} />
          ))}
        </div>
      )}
    </div>
  );
};

const CommentThread = ({ comments, setComments, apiBase }) => {
  const [openReply, setOpenReply] = useState(null);

  const likeComment = async (comment) => {
    const key = `clike_${comment.id}`;
    const liked = localStorage.getItem(key) === "1";
    const delta = liked ? -1 : 1;
    setComments((prev) =>
      mapComments(prev, comment.id, (c) => ({ ...c, likes: Math.max(0, (c.likes || 0) + delta) }))
    );
    localStorage.setItem(key, liked ? "0" : "1");
    try {
      const res = await axios.post(`${apiBase}/comment/${comment.id}/like`, { delta });
      setComments((prev) => mapComments(prev, comment.id, (c) => ({ ...c, likes: res.data.likes })));
    } catch (err) {
      setComments((prev) =>
        mapComments(prev, comment.id, (c) => ({ ...c, likes: Math.max(0, (c.likes || 0) - delta) }))
      );
      localStorage.setItem(key, liked ? "1" : "0");
    }
  };

  const submitReply = async (parentId, payload) => {
    try {
      const res = await axios.post(`${apiBase}/comment/${parentId}/reply`, payload);
      setComments((prev) =>
        mapComments(prev, parentId, (c) => ({ ...c, replies: [...(c.replies || []), res.data] }))
      );
      setOpenReply(null);
      return true;
    } catch (err) {
      toast.error("Couldn't post your reply. Try again.");
      return false;
    }
  };

  if (!comments.length) {
    return <p className="font-display italic text-neutral-500">No comments yet. Start the conversation.</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      {comments.map((c) => (
        <CommentItem
          key={c.id}
          comment={c}
          openReply={openReply}
          setOpenReply={setOpenReply}
          onLike={likeComment}
          onReply={submitReply}
        />
      ))}
    </div>
  );
};

export default CommentThread;
